/**
 * Pays du portefeuille hors France (Suisse, Belgique, Luxembourg) : reconnaissance du pays saisi,
 * nettoyage des codes postaux et des villes telles qu'elles arrivent de SharePoint ("1218 CH",
 * "L-8070", "Fully/Martigny"...).
 */

export const PAYS_ETRANGERS = ['CH', 'BE', 'LU'] as const;
export type PaysEtranger = (typeof PAYS_ETRANGERS)[number];
export type CodePays = 'FR' | PaysEtranger;

/** Nom affiché de chaque pays. */
export const NOMS_PAYS: Record<CodePays, string> = {
  FR: 'France',
  CH: 'Suisse',
  BE: 'Belgique',
  LU: 'Luxembourg',
};

function normaliser(texte: string | null | undefined): string {
  return (texte ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Noms reconnus de chaque pays (normalisés : minuscules, sans accents). */
const NOMS_RECONNUS: Record<CodePays, string[]> = {
  FR: ['france', 'fr', 'fra'],
  CH: ['suisse', 'ch', 'che', 'schweiz', 'svizzera', 'switzerland', 'confederation suisse'],
  BE: ['belgique', 'be', 'bel', 'belgie', 'belgien', 'belgium'],
  LU: ['luxembourg', 'lu', 'lux', 'luxemburg', 'letzebuerg', 'grand duche de luxembourg'],
};

/**
 * Code du pays saisi, ou `null` s'il n'est pas reconnu. Un pays vide vaut France (valeur par
 * défaut de la base), comme `estFrance`.
 */
export function codePays(pays: string | null | undefined): CodePays | null {
  const p = normaliser(pays);
  if (p === '') return 'FR';
  for (const [code, noms] of Object.entries(NOMS_RECONNUS) as [CodePays, string[]][]) {
    if (noms.includes(p)) return code;
  }
  return null;
}

/** Vrai si le pays est un pays du portefeuille hors France. */
export function estPaysEtranger(code: CodePays | null): code is PaysEtranger {
  return code !== null && code !== 'FR';
}

/**
 * Code postal à 4 chiffres d'un client suisse, belge ou luxembourgeois, ou `null`. Tolère les
 * préfixes et suffixes de pays ("1218 CH", "CH-1218", "L-8070", "B-4450") ; un texte sans
 * groupe de 4 chiffres ("Suisse", "CH") ne donne rien.
 */
export function codePostalEtranger(codePostal: string | null | undefined): string | null {
  const groupes = (codePostal ?? '').match(/\d+/g) ?? [];
  const [unique] = groupes;
  return groupes.length === 1 && unique !== undefined && unique.length === 4 ? unique : null;
}

/**
 * Villes à essayer pour un client : la saisie entière, puis chacune de ses parties quand elle en
 * contient plusieurs ("Fully/Martigny", "Bomal / Ourthe"). Sans doublon, dans l'ordre de saisie.
 */
export function villesCandidates(ville: string | null | undefined): string[] {
  const entiere = (ville ?? '').trim();
  if (entiere === '') return [];
  const parties = entiere
    .split(/\s*[/,;]\s*/)
    .map((p) => p.trim())
    .filter((p) => p !== '');
  return [...new Set([entiere, ...parties])];
}

/** Clé de comparaison d'un nom de localité : minuscules, sans accents, tirets et apostrophes en espaces. */
export function cleLocalite(nom: string | null | undefined): string {
  return normaliser(nom);
}
